/**
 * E007-T022: InputMappingGrid Component
 *
 * The workflow's inputs and the source column that fills each of them, as
 * pairs: on the left the column choice (the table's side), on the right the
 * workflow input it fills. "Auto-Zuordnung" proposes the columns for all
 * open inputs.
 */

import { Button, notify } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { AiIcon } from '@blocksuite/icons/rc';
import type { InputMapping } from '@streamlain/field-types';
import {
  checkCompatibility,
  hasValidRelationInputMappings,
  INPUT_MAPPING_OPTIONS_SOURCE,
  type InputMappingSource,
  isOptionsInputMapping,
  normalizeType,
  RELATION_ROWS_COLUMN_ID,
} from '@streamlain/field-types';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type { ColumnInfo } from '../../services/database-discovery';
import type { WorkflowSchemaField } from '../../services/workflow-client';
import {
  WEBHOOK_PAYLOAD_COLUMN_ID,
  WEBHOOK_PAYLOAD_TABLE_ID,
} from '../../types';
import { AutoMatchDialog } from './auto-match-dialog';
import { MappingCell } from './mapping-cell';
import {
  SectionEmpty,
  SectionHint,
  SectionMappingGrid,
  SectionMappingPair,
  SectionStack,
} from './section-layout';
import { CompatDot } from './type-chip';
import type { FieldOptionGroup } from './typed-field-select';
import { TypedFieldSelect } from './typed-field-select';

const AUTOMATION_MAPPING_LIMIT = 32;
/** Choice id of "Options of <column>" for a select column of the main table. */
const OPTIONS_CHOICE_PREFIX = '__options__::';

// ============================================================================
// Types
// ============================================================================

export type { InputMapping };

/** sl-m379: Column group from a specific input table */
export interface ColumnGroup {
  /** Stable InputTable.id used for inputMappings[].inputTableId */
  inputTableId: string;
  /** Physical database/table block id (kept for compatibility/UI grouping) */
  tableId?: string;
  /** Display label (e.g. "Lead", "Prompts", "Kunden") */
  label: string;
  /** Columns from this table */
  columns: ColumnInfo[];
  /** sl-i6t9: Relation cardinality — 'many' produces array fields in payload */
  cardinality?: 'one' | 'many';
  linkedVia?: string;
}

interface InputMappingGridProps {
  /** Schema input fields (required + optional) */
  schemaFields: {
    required: WorkflowSchemaField[];
    optional: WorkflowSchemaField[];
  };
  /** Available source columns (lead table — backwards compatible) */
  sourceColumns: ColumnInfo[];
  /** Current input mappings */
  mappings: InputMapping[];
  /** Callback when mappings change */
  onMappingsChange: (mappings: InputMapping[]) => void;
  /** sl-m379: Additional column groups from secondary tables */
  secondaryColumnGroups?: ColumnGroup[];
  /** Display name for the source (lead) table */
  sourceTableName?: string;
  /** When set, adds a "Webhook: <name>" group with a single "payload" (json) option */
  webhookGroup?: { name: string };
}

// (CompatibilityBadge and Menu config moved to shared TypedFieldSelect)

// ============================================================================
// Main Component
// ============================================================================

export const InputMappingGrid: FC<InputMappingGridProps> = ({
  schemaFields,
  sourceColumns,
  mappings,
  onMappingsChange,
  secondaryColumnGroups,
  sourceTableName,
  webhookGroup,
}) => {
  const t = useI18n();
  const [isAutoMatchOpen, setIsAutoMatchOpen] = useState(false);

  // All fields combined with required flag
  const allFields = useMemo(() => {
    const required = schemaFields.required.map(f => ({
      ...f,
      isRequired: true,
    }));
    const optional = schemaFields.optional.map(f => ({
      ...f,
      isRequired: false,
    }));
    return [...required, ...optional];
  }, [schemaFields]);

  // Build mapping lookup: schemaFieldId → { columnId, inputTableId }
  const mappingLookup = useMemo(() => {
    const map = new Map<
      string,
      {
        columnId: string | null;
        inputTableId?: string;
        source?: InputMappingSource;
      }
    >();
    for (const m of mappings) {
      map.set(m.schemaFieldId, {
        columnId: m.columnId,
        inputTableId: m.inputTableId,
        source: m.source,
      });
    }
    return map;
  }, [mappings]);

  // Get column by ID + optional inputTableId for disambiguation
  const getColumn = useCallback(
    (
      columnId: string | null,
      inputTableId?: string,
      source?: InputMappingSource
    ): ColumnInfo | null => {
      if (!columnId) return null;
      // The options of a main-table column arrive as a list of labels.
      if (isOptionsInputMapping({ source })) {
        const column = sourceColumns.find(c => c.id === columnId);
        return column ? { ...column, type: 'multi-select' } : null;
      }
      // Webhook payload sentinel
      if (inputTableId === WEBHOOK_PAYLOAD_TABLE_ID) {
        return {
          id: WEBHOOK_PAYLOAD_COLUMN_ID,
          name: WEBHOOK_PAYLOAD_COLUMN_ID,
          type: 'json',
        } as ColumnInfo;
      }
      // If inputTableId is explicitly '' or undefined, search lead first
      if (!inputTableId || inputTableId === '') {
        const fromLead = sourceColumns.find(c => c.id === columnId);
        if (fromLead) return fromLead;
      }
      if (inputTableId) {
        const group = secondaryColumnGroups?.find(
          group => group.inputTableId === inputTableId
        );
        return group && columnId === RELATION_ROWS_COLUMN_ID
          ? ({
              id: RELATION_ROWS_COLUMN_ID,
              name: group.label,
              type: 'json',
            } as ColumnInfo)
          : null;
      }
      return null;
    },
    [sourceColumns, secondaryColumnGroups]
  );

  // Handle mapping change — inputTableId is passed explicitly to avoid
  // ambiguity when multiple tables share the same column ID (e.g. 'title').
  const handleMappingChange = useCallback(
    (
      schemaFieldId: string,
      columnId: string | null,
      inputTableId?: string,
      source?: InputMappingSource
    ) => {
      const field = allFields.find(f => f.id === schemaFieldId);
      const newMappings = [...mappings];
      const existingIndex = newMappings.findIndex(
        m => m.schemaFieldId === schemaFieldId
      );

      if (existingIndex >= 0) {
        if (columnId === null) {
          newMappings.splice(existingIndex, 1);
        } else {
          newMappings[existingIndex] = {
            schemaFieldId,
            schemaFieldName: field?.field,
            columnId,
            inputTableId,
            ...(source ? { source } : {}),
          };
        }
      } else if (columnId !== null) {
        if (newMappings.length >= AUTOMATION_MAPPING_LIMIT) {
          notify.warning({
            title: t['com.streamlain.streamlain.mapping.limit-reached.title'](),
            message: t['com.streamlain.streamlain.mapping.limit-reached.input'](
              {
                count: String(AUTOMATION_MAPPING_LIMIT),
              }
            ),
          });
          return;
        }
        newMappings.push({
          schemaFieldId,
          schemaFieldName: field?.field,
          columnId,
          inputTableId,
          ...(source ? { source } : {}),
        });
      }

      onMappingsChange(newMappings);
    },
    [allFields, mappings, onMappingsChange, t]
  );

  // Build FieldOptionGroup[] from source columns + secondary groups
  const columnGroups = useMemo((): FieldOptionGroup[] => {
    const groups: FieldOptionGroup[] = [];
    // Webhook source group (prepended — shown first in dropdown)
    if (webhookGroup) {
      groups.push({
        id: WEBHOOK_PAYLOAD_TABLE_ID,
        label: `Webhook: ${webhookGroup.name}`,
        options: [
          {
            id: `${WEBHOOK_PAYLOAD_TABLE_ID}::${WEBHOOK_PAYLOAD_COLUMN_ID}`,
            label: WEBHOOK_PAYLOAD_COLUMN_ID,
            type: 'json',
          },
        ],
      });
    }
    // Lead table group
    if (sourceColumns.length > 0) {
      groups.push({
        id: '_lead',
        label:
          sourceTableName ||
          t['com.streamlain.automation.sidebar.mapping.source-table'](),
        options: [
          ...sourceColumns.map(col => ({
            id: col.id,
            label: col.name,
            type: col.type,
          })),
          ...sourceColumns
            .filter(col => col.type === 'select' || col.type === 'multi-select')
            .map(col => ({
              id: `${OPTIONS_CHOICE_PREFIX}${col.id}`,
              label: t[
                'com.streamlain.automation.sidebar.mapping.column-options'
              ]({ column: col.name }),
              type: 'multi-select',
            })),
        ],
      });
    }
    // Secondary table groups
    if (secondaryColumnGroups) {
      for (const group of secondaryColumnGroups) {
        groups.push({
          id: group.inputTableId,
          label: group.label,
          options: [
            {
              id: `${group.inputTableId}::${RELATION_ROWS_COLUMN_ID}`,
              label: group.label,
              type: 'json',
            },
          ],
        });
      }
    }
    return groups;
  }, [sourceColumns, secondaryColumnGroups, sourceTableName, t, webhookGroup]);

  // Count missing required fields
  const missingRequired = useMemo(() => {
    return schemaFields.required.filter(
      f =>
        !mappingLookup.has(f.id) || mappingLookup.get(f.id)?.columnId === null
    );
  }, [schemaFields.required, mappingLookup]);

  const hasAutoMapData =
    columnGroups.some(group => group.options.length > 0) &&
    allFields.length > 0;
  const isRelationSource = (tableId?: string) =>
    !!tableId && tableId !== WEBHOOK_PAYLOAD_TABLE_ID;
  const validMapping = (
    columnId: string | null,
    tableId: string | undefined,
    type: string,
    source?: InputMappingSource
  ) =>
    hasValidRelationInputMappings(
      [{ schemaFieldId: 'target', columnId, inputTableId: tableId, source }],
      secondaryColumnGroups?.map(group => ({
        id: group.inputTableId,
        role: 'secondary',
        linkedVia: group.linkedVia,
      })),
      [{ id: 'target', type }]
    );
  const hasInvalidRelationMapping = mappings.some(
    mapping =>
      mapping.columnId &&
      (!validMapping(
        mapping.columnId,
        mapping.inputTableId,
        allFields.find(field => field.id === mapping.schemaFieldId)?.type ?? '',
        mapping.source
      ) ||
        (mapping.columnId === RELATION_ROWS_COLUMN_ID &&
          !isRelationSource(mapping.inputTableId)))
  );
  if (allFields.length === 0) {
    return (
      <SectionEmpty>
        {t['com.streamlain.streamlain.input.no-schema-fields']()}
      </SectionEmpty>
    );
  }

  return (
    <SectionStack>
      {/* Auto-Match Dialog */}
      {isAutoMatchOpen && (
        <AutoMatchDialog
          open={isAutoMatchOpen}
          onClose={() => setIsAutoMatchOpen(false)}
          sourceFields={columnGroups.flatMap(group =>
            group.options
              .filter(option => !option.id.startsWith(OPTIONS_CHOICE_PREFIX))
              .map(option => ({
                path: option.id,
                type: option.type,
                displayName: option.label,
              }))
          )}
          isMappingAllowed={(source, target) => {
            const [tableId, columnId] = source.path.includes('::')
              ? source.path.split('::')
              : ['', source.path];
            return validMapping(columnId, tableId, target.type);
          }}
          targetFields={allFields.map(f => ({
            id: f.id,
            name: f.field,
            type: f.type,
          }))}
          existingTargetIds={
            new Set(mappings.filter(m => m.columnId).map(m => m.schemaFieldId))
          }
          sourceLabel={
            webhookGroup
              ? t['com.streamlain.automation.sidebar.mapping.side-source']()
              : t['com.streamlain.automation.sidebar.mapping.side-table']()
          }
          targetLabel={t[
            'com.streamlain.automation.sidebar.mapping.side-workflow'
          ]()}
          workflowSide="target"
          onApply={suggestions => {
            const validSuggestions = suggestions.filter(suggestion => {
              const [tableId, columnId] = suggestion.responseField.includes(
                '::'
              )
                ? suggestion.responseField.split('::')
                : ['', suggestion.responseField];
              return validMapping(
                columnId,
                tableId,
                allFields.find(field => field.id === suggestion.targetColumnId)
                  ?.type ?? ''
              );
            });
            const matchedIds = new Set(
              validSuggestions.map(s => s.targetColumnId)
            );
            const preserved = mappings.filter(
              m => !matchedIds.has(m.schemaFieldId)
            );
            const availableSlots = Math.max(
              0,
              AUTOMATION_MAPPING_LIMIT - preserved.length
            );
            const limitedSuggestions = validSuggestions.slice(
              0,
              availableSlots
            );
            if (limitedSuggestions.length < suggestions.length) {
              notify.warning({
                title:
                  t['com.streamlain.streamlain.mapping.limit-reached.title'](),
                message: t[
                  'com.streamlain.streamlain.mapping.limit-reached.input'
                ]({
                  count: String(AUTOMATION_MAPPING_LIMIT),
                }),
              });
            }
            const newMappings = limitedSuggestions.map(s => {
              const field = allFields.find(f => f.id === s.targetColumnId);
              return {
                schemaFieldId: s.targetColumnId,
                schemaFieldName: field?.field,
                columnId: s.responseField.includes('::')
                  ? s.responseField.split('::')[1]
                  : s.responseField,
                inputTableId: s.responseField.includes('::')
                  ? s.responseField.split('::')[0]
                  : undefined,
              };
            });
            onMappingsChange([...preserved, ...newMappings]);
          }}
        />
      )}

      {hasAutoMapData && (
        <Button
          block
          prefix={<AiIcon />}
          onClick={() => setIsAutoMatchOpen(true)}
          data-testid="input-auto-match"
          data-attention-action
        >
          {t['com.streamlain.streamlain.automap.button']()}
        </Button>
      )}
      {hasInvalidRelationMapping && (
        <SectionHint tone="warning">
          <span role="alert">
            {t['com.streamlain.automation.sidebar.relation-mapping-invalid']()}
          </span>
        </SectionHint>
      )}
      {missingRequired.length > 0 && (
        <SectionHint tone="warning">
          {t['com.streamlain.automation.sidebar.mapping.required-open']({
            count: String(missingRequired.length),
          })}
        </SectionHint>
      )}

      <SectionMappingGrid
        sourceLabel={
          webhookGroup
            ? t['com.streamlain.automation.sidebar.mapping.side-source']()
            : t['com.streamlain.automation.sidebar.mapping.side-table']()
        }
        targetLabel={t[
          'com.streamlain.automation.sidebar.mapping.side-workflow'
        ]()}
        workflowSide="target"
      >
        {allFields.map(field => {
          const mapping = mappingLookup.get(field.id);
          const columnId = mapping?.columnId ?? null;
          const mappedTableId = mapping?.inputTableId;
          const mappedSource = mapping?.source;
          const column = getColumn(columnId, mappedTableId, mappedSource);
          const compatibility = column
            ? checkCompatibility(field.type, column.type)
            : 'incompatible';

          return (
            <SectionMappingPair
              key={field.id}
              fixed="target"
              link={
                columnId !== null ? (
                  <CompatDot compatibility={compatibility} />
                ) : undefined
              }
              source={
                <TypedFieldSelect
                  stacked
                  invalid={field.isRequired && columnId === null}
                  value={
                    columnId
                      ? isOptionsInputMapping({ source: mappedSource })
                        ? `${OPTIONS_CHOICE_PREFIX}${columnId}`
                        : mappedTableId && mappedTableId !== ''
                          ? `${mappedTableId}::${columnId}`
                          : columnId
                      : ''
                  }
                  groups={columnGroups.filter(
                    group =>
                      group.id === '_lead' ||
                      group.id === WEBHOOK_PAYLOAD_TABLE_ID ||
                      normalizeType(field.type) === 'json'
                  )}
                  onChange={optionId => {
                    if (!optionId) {
                      handleMappingChange(field.id, null);
                    } else if (optionId.startsWith(OPTIONS_CHOICE_PREFIX)) {
                      handleMappingChange(
                        field.id,
                        optionId.slice(OPTIONS_CHOICE_PREFIX.length),
                        '',
                        INPUT_MAPPING_OPTIONS_SOURCE
                      );
                    } else if (optionId.includes('::')) {
                      const [tableId, colId] = optionId.split('::');
                      if (validMapping(colId, tableId, field.type))
                        handleMappingChange(field.id, colId, tableId);
                    } else {
                      handleMappingChange(field.id, optionId, '');
                    }
                  }}
                  placeholder={t[
                    'com.streamlain.automation.sidebar.mapping.pick-column'
                  ]()}
                  ariaLabel={field.field}
                  targetType={field.type}
                />
              }
              target={
                <MappingCell
                  name={field.field}
                  type={field.type}
                  required={field.isRequired}
                  title={field.description}
                />
              }
            />
          );
        })}
      </SectionMappingGrid>
    </SectionStack>
  );
};
